import type { Dispatch, SetStateAction } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useFormikContext } from 'formik'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  paymentEntitiesQuery,
  savedAutoPayMethodQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import type { PayMethod } from '@b-stock/payments-methods-api-client'
import { AutoPayPaymentMethodPaymentTypeEnum } from '@b-stock/payments-methods-api-client'
import type { ActiveServiceDto } from '@b-stock/subscription-api-client'

import { useAutoPaymentMethods } from '@/buyer-portal/components/features/listing/hooks/useAutoPaymentMethods'
import { calculateConvenienceFee } from '@/buyer-portal/components/features/order/checkout/helpers/prepareCheckoutProps'
import { isAchMethod } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import {
  isSellerCollectsLot,
  isWireRefused,
  useIsSellerCollectsSale,
} from '@/buyer-portal/helpers/useIsSellerCollectsSale'
import { FeatureFlags } from '@/helpers/featureFlags'

import {
  calculateEstimatedTotalPayment,
  getAutoPayTarget,
  getHasAutoPay,
} from '../utils'
import type { BidFormFormikValues } from './BidFormFormikProvider'

type AutoPayMethodContextValue = {
  defaultPaymentMethod: PayMethod | null
  paymentMethods: PayMethod[]
  isSavedMethodInvalid: boolean
  selectedPaymentMethodId: string | null
  setSelectedPaymentMethodId: Dispatch<SetStateAction<string | null>>
  isWireSelected: boolean
  setIsWireSelected: Dispatch<SetStateAction<boolean>>
  payableAmountInCents: number
  listingIsCreditCardEnabled: boolean
  showAchBlockedWireFallbackMessage: boolean
}

const noop = () => {}

const defaultValue: AutoPayMethodContextValue = {
  defaultPaymentMethod: null,
  paymentMethods: [],
  isSavedMethodInvalid: false,
  selectedPaymentMethodId: null,
  setSelectedPaymentMethodId: noop,
  isWireSelected: false,
  setIsWireSelected: noop,
  payableAmountInCents: 0,
  listingIsCreditCardEnabled: false,
  showAchBlockedWireFallbackMessage: false,
}

const AutoPayMethodContext =
  createContext<AutoPayMethodContextValue>(defaultValue)

export const AutoPayMethodProvider = ({
  children,
  listing,
  selectedQuote,
  sellerSubscription,
  buyNowPrice,
}: {
  children: React.ReactNode
  listing: OrchestratedAuctionListingDetails
  selectedQuote?: OrchestratedQuote | null
  sellerSubscription?: ActiveServiceDto | null
  buyNowPrice?: number | null
}) => {
  const accountId = useAccountId()
  const formikContext = useFormikContext<BidFormFormikValues>()
  const bidAmount = formikContext.values.amount || buyNowPrice || 0
  const autoPayTarget = getAutoPayTarget(listing)
  // Non-USD listings only support Wire Transfer (matches the isNonUsdCurrency
  // restriction enforced in BidConfirmationModal/BuyNowConfirmationModal) —
  // a saved digital default must never be auto-selected for them.
  const isNonUsdCurrency =
    !!listing.currency && listing.currency.toUpperCase() !== 'USD'

  // FF autoPayEnforced: also fetch payment methods when FF is on
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)

  // Wire pays for goods, and on a Seller Collects listing B-Stock collects
  // none. The picker refuses it, but the saved autopay preference reaches the
  // confirmation modals without the picker ever being opened, so the refusal
  // has to start here — this is the upstream both modals read.
  //
  // Except on a non-USD listing, where ACH, card and net terms are all
  // restricted and wire is the only instrument left. Refusing it there would
  // leave the buyer with no way to pay the B-Stock fee at all, which is worse
  // than offering the one method that works.
  const refusesWire = isWireRefused({
    isSellerCollectsSale: useIsSellerCollectsSale(listing),
    currency: listing.currency,
  })
  const hasAutoPay = getHasAutoPay(listing, selectedQuote)

  const bStockFeeInCents = bidAmount * (sellerSubscription?.buyersPremium ?? 0)

  const estimatedTotal = calculateEstimatedTotalPayment(
    bidAmount,
    selectedQuote,
    bStockFeeInCents
  )

  // The same rule the breakdown applies, from the same predicate: on a Seller
  // Collects lot there is no payment to B-Stock for the goods, so there is
  // nothing for a processor fee to sit on. This figure decides net-terms
  // eligibility against a credit limit, so carrying a fee the breakdown no
  // longer shows could refuse a method over money nobody is owed.
  const payableAmountInCents =
    estimatedTotal +
    (isSellerCollectsLot(listing) ? 0 : calculateConvenienceFee(estimatedTotal))

  const queryClient = useQueryClient()

  // FF autoPayEnforced: refresh payment methods and saved preference when user returns from another tab
  useEffect(() => {
    if (!autoPayEnforced) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void queryClient.invalidateQueries({
          queryKey: [paymentEntitiesQuery.name],
        })
        void queryClient.invalidateQueries({
          queryKey: [savedAutoPayMethodQuery.name],
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [autoPayEnforced, queryClient])

  const {
    defaultPaymentMethod,
    paymentMethods,
    savedPaymentType,
    isSavedMethodInvalid,
    isSavedAutoPayFetched,
    isSavedAutoPayFetching,
    isSavedAchBlocked,
    isWireFallbackAvailableForSavedAch,
    isLoading,
  } = useAutoPaymentMethods({
    accountId,
    enabled: !!autoPayTarget && (autoPayEnforced || hasAutoPay),
    payableAmountInCents,
    payForId: autoPayTarget?.payForId ?? listing._id,
    payForType: autoPayTarget?.payForType ?? 'listing',
    listingIsCreditCardEnabled: !!listing.isCreditCardEnabled,
  })

  // Persists the user's PM selection across modal open/close within the same session
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null)

  // Persists wire selection across modal open/close within the same session
  const [isWireSelected, setIsWireSelected] = useState(false)

  const hasExplicitSelection = useRef(false)

  const shouldUseWireFallbackForBlockedAch =
    isWireFallbackAvailableForSavedAch === true

  // Clear stale digital selection when the method no longer exists after a refresh
  // (e.g. deleted in another tab), or when selected ACH is no longer usable.
  // Wire selection is unaffected since it has no PM id.
  useEffect(() => {
    if (selectedPaymentMethodId === null || paymentMethods.length === 0) return
    const selectedMethod = paymentMethods.find(
      (pm) => pm.id === selectedPaymentMethodId
    )
    if (!selectedMethod) {
      setSelectedPaymentMethodId(null) // oxlint-disable-line react/set-state-in-effect
      return
    }

    if (isSavedAchBlocked && isAchMethod(selectedMethod.type)) {
      setSelectedPaymentMethodId(null)
      if (shouldUseWireFallbackForBlockedAch && !refusesWire) {
        setIsWireSelected(true)
      }
    }
  }, [
    selectedPaymentMethodId,
    paymentMethods,
    isSavedAchBlocked,
    shouldUseWireFallbackForBlockedAch,
    refusesWire,
  ])

  // The selection effect bails out while wire is held, so a gate that resolves
  // after the saved-Wire branch has already run would never undo it.
  useEffect(() => {
    if (refusesWire) setIsWireSelected(false) // oxlint-disable-line react/set-state-in-effect
  }, [refusesWire])

  useEffect(() => {
    if (isLoading || !isSavedAutoPayFetched || isSavedAutoPayFetching) return
    if (hasExplicitSelection.current) return
    if (isWireSelected) return

    if (
      !refusesWire &&
      (isNonUsdCurrency ||
        savedPaymentType === AutoPayPaymentMethodPaymentTypeEnum.Wire ||
        shouldUseWireFallbackForBlockedAch)
    ) {
      setSelectedPaymentMethodId(null) // oxlint-disable-line react/set-state-in-effect
      setIsWireSelected(true)
    } else if (selectedPaymentMethodId === null && defaultPaymentMethod?.id) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id)
    }
  }, [
    isLoading,
    isSavedAutoPayFetched,
    isSavedAutoPayFetching,
    savedPaymentType,
    selectedPaymentMethodId,
    isWireSelected,
    defaultPaymentMethod?.id,
    isNonUsdCurrency,
    shouldUseWireFallbackForBlockedAch,
    refusesWire,
  ])

  const value: AutoPayMethodContextValue = {
    defaultPaymentMethod: defaultPaymentMethod ?? null,
    paymentMethods,
    isSavedMethodInvalid,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId: (selection) => {
      hasExplicitSelection.current = true
      setSelectedPaymentMethodId(selection)
    },
    isWireSelected,
    setIsWireSelected: (selection) => {
      hasExplicitSelection.current = true
      setIsWireSelected(selection)
    },
    payableAmountInCents,
    listingIsCreditCardEnabled: !!listing.isCreditCardEnabled,
    showAchBlockedWireFallbackMessage:
      shouldUseWireFallbackForBlockedAch &&
      isWireSelected &&
      selectedPaymentMethodId === null,
  }

  return (
    <AutoPayMethodContext.Provider value={value}>
      {children}
    </AutoPayMethodContext.Provider>
  )
}

export const useAutoPayMethodContext = () => useContext(AutoPayMethodContext)
